<template>
  <BaseModal
    :model-value="modelValue"
    @update:model-value="handleClose"
    :title="layout === 'centered' ? '' : title"
    :show-close-button="layout !== 'centered'"
    size="small"
    :overlay-z-index="10050"
  >
    <!-- Centered layout: icon + title + message + preview (matches reference DeleteAccountModal) -->
    <template v-if="layout === 'centered'">
      <div
        v-if="resolvedCenteredIcon === 'danger'"
        class="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4"
      >
        <i class="ri-delete-bin-line text-red-400 text-2xl" aria-hidden="true" />
      </div>
      <div
        v-else-if="resolvedCenteredIcon === 'info'"
        class="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4"
      >
        <i class="ri-information-line text-blue-400 text-2xl" aria-hidden="true" />
      </div>
      <h3 class="text-lg font-semibold text-text text-center mb-3">{{ title }}</h3>
      <div class="border-b border-border mb-4" aria-hidden="true" />
      <div class="text-sm text-text-secondary text-center mb-6 space-y-3">
        <slot name="message">
          <p v-if="message">{{ message }}</p>
        </slot>
        <p v-if="warningText" class="text-amber-400">{{ warningText }}</p>
      </div>
      <div v-if="$slots.preview" class="bg-surface-muted rounded-lg p-4 mb-6">
        <slot name="preview" />
      </div>
    </template>
    <!-- Default layout: message + preview + warning -->
    <template v-else>
      <div class="space-y-4">
        <BaseText size="medium" class="text-text">
          <slot name="message">{{ message }}</slot>
        </BaseText>
        <div v-if="$slots.preview" class="preview-slot">
          <slot name="preview" />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center gap-3 w-full">
        <button
          type="button"
          class="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-text-secondary hover:bg-surface-hover transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="loading"
          @click="handleCancel"
        >
          {{ cancelLabel }}
        </button>
        <button
          v-if="confirmVariant === 'danger'"
          type="button"
          class="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="loading"
          @click="handleConfirm"
        >
          {{ loading ? loadingLabel : confirmLabel }}
        </button>
        <BaseButton
          v-else
          :variant="confirmVariant"
          class="flex-1"
          :disabled="loading"
          @click="handleConfirm"
        >
          {{ loading ? loadingLabel : confirmLabel }}
        </BaseButton>
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseModal from '@/components/feedback/BaseModal.vue'
import BaseButton from '@/components/navigation/BaseButton.vue'
import BaseText from '@/components/ui/BaseText.vue'

interface Props {
  modelValue: boolean
  title?: string
  message?: string
  warningText?: string
  confirmLabel?: string
  cancelLabel?: string
  loadingLabel?: string
  confirmVariant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'purple' | 'ghost'
  loading?: boolean
  /** Icon shown above the title in centered layout. Defaults to delete icon when confirmVariant is danger. */
  centeredIcon?: 'danger' | 'info'
  /** When "centered", matches reference design: no header, icon + title + message + preview, full-width buttons */
  layout?: 'default' | 'centered'
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Confirm Action',
  message: 'Are you sure you want to proceed?',
  warningText: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  loadingLabel: 'Processing...',
  confirmVariant: 'danger',
  loading: false,
  layout: undefined
})

const layout = computed(() => props.layout ?? (props.confirmVariant === 'danger' ? 'centered' : 'default'))

const resolvedCenteredIcon = computed((): 'danger' | 'info' | null => {
  if (props.centeredIcon) {
    return props.centeredIcon
  }
  if (props.confirmVariant === 'danger') {
    return 'danger'
  }
  return null
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: []
  cancel: []
}>()

const handleClose = () => {
  if (!props.loading) {
    emit('update:modelValue', false)
  }
}

const handleCancel = () => {
  emit('cancel')
  handleClose()
}

const handleConfirm = () => {
  emit('confirm')
}
</script>

<style scoped>
.preview-slot {
  margin: 0.5rem 0;
}
</style>

